'use client';

import { useMemo } from 'react';
import { CheckOverCircle } from 'assets/icons';
import { CurrencySymbol, useFormatPrice } from 'hooks/pricing/useFormattedPrice';
import type { Currency } from 'utils/Currency';
import { useBuyContext } from '../../state/BuyContext';
import { useBuySelection } from '../../state/useBuySelection';
import { resolve, type LargePromptCardConfig } from '../../state/cardSchema';
import { Section } from '../Section';
import { Separator } from '../Separator';
import { InfoBanner } from '../InfoBanner';
import styles from './LargePromptCard.module.css';

export type LargePromptCardProps = {
  card: LargePromptCardConfig;
};

export const LargePromptCard = ({ card }: LargePromptCardProps) => {
  const { resolveCtx, currency } = useBuyContext();
  const { selected, select } = useBuySelection(card.id);

  const symbol = CurrencySymbol[currency.toLowerCase() as Currency];

  const bannerText = useMemo(
    () => (card.banner ? resolve(card.banner.text, resolveCtx) : null),
    [card.banner, resolveCtx],
  );
  const formatPriceWithComma = useFormatPrice();

  return (
    <Section
      id={card.id}
      title={card.title}
      subtitle={card.subtitle}
      info={card.info}
    >
      {card.banner && bannerText ? (
        <InfoBanner icon={card.banner.icon}>{bannerText}</InfoBanner>
      ) : null}

      <div className={styles.container}>
        {card.options.map((option) => {
          const isActive = selected === option.id;
          const disabled = !!resolve(option.disabled, resolveCtx);
          const price = resolve(option.price, resolveCtx);
          const strikedPrice = resolve(option.strikedPrice, resolveCtx);
          const saveTag = resolve(option.saveTag, resolveCtx);
          const secondaryText = resolve(option.secondaryText, resolveCtx);

          if (option.isDecline) {
            return (
              <button
                key={option.id}
                type="button"
                className={`${styles.declineButton}${isActive ? ` ${styles.active}` : ''}`}
                onClick={() => select(option.id)}
                disabled={disabled}
                aria-pressed={isActive}
              >
                <span>
                  <strong>{option.title}</strong>
                </span>
              </button>
            );
          }

          return (
            <button
              key={option.id}
              type="button"
              className={`${styles.promptButton}${isActive ? ` ${styles.active}` : ''}${option.label ? ` ${styles.paddingTop}` : ''}`}
              onClick={() => select(option.id)}
              disabled={disabled}
              aria-pressed={isActive}
            >
              {option.label ? <div className={styles.label}>{option.label}</div> : null}

              <div className={styles['content-wrapper']}>
                <div className={styles.content}>
                  <div className={styles['title-bar']}>
                    <div className={styles.title}>
                      <span>
                        <strong>{option.title}</strong>
                      </span>
                      {saveTag ? <span className={styles['save-tag']}>{saveTag}</span> : null}
                    </div>

                    <div className={styles.disclaimer}>
                      <div className={styles.price}>
                        {typeof strikedPrice === 'number' && strikedPrice > (price ?? 0) ? (
                          <span style={{ textDecoration: 'line-through', marginRight: 6 }}>
                            <>
                              {symbol}
                              {formatPriceWithComma(strikedPrice)}
                            </>
                          </span>
                        ) : null}
                        {typeof price === 'number' ? (
                          price === 0 ? (
                            <strong>FREE</strong>
                          ) : (
                            <>
                              {symbol}
                              {formatPriceWithComma(price)}
                            </>
                          )
                        ) : null}
                      </div>
                      {secondaryText ? (
                        <div className={styles['disclaimer-text']}>
                          <span>{secondaryText}</span>
                        </div>
                      ) : null}
                    </div>
                  </div>

                  {option.bullets && option.bullets.length > 0 ? (
                    <>
                      <Separator transparent size="sm" />
                      <div className={styles.list}>
                        {option.bullets.map((bullet, idx) => (
                          <p key={idx}>
                            <CheckOverCircle className={styles.checkmark} />
                            {bullet}
                          </p>
                        ))}
                      </div>
                    </>
                  ) : null}
                </div>
              </div>
            </button>
          );
        })}
      </div>
    </Section>
  );
};
